// Playbook (034) — shared constants/helpers for the Formation/Front/Unit
// library. Mirrors utils/teamSchedule.js's/teamClassification.js's role
// as the one centralized place for labels/formatting, so pages never
// re-derive the same thing independently.

import { FORMAT_OPTIONS } from './teamClassification';

export const UNIT_TYPES = ['offense', 'defense', 'special_teams'];

export const UNIT_TYPE_LABELS = {
  offense: 'Offense',
  defense: 'Defense',
  special_teams: 'Special Teams',
};

// The reusable-structure TERM differs per book (product decision) even
// though all three are the same underlying playbook_formations row,
// discriminated only by unit_type — this is UI vocabulary only, never a
// schema/data difference.
const STRUCTURE_TERM_SINGULAR = { offense: 'Formation', defense: 'Front', special_teams: 'Unit' };
const STRUCTURE_TERM_PLURAL = { offense: 'Formations', defense: 'Fronts', special_teams: 'Units' };

export function structureTermLabel(unitType) {
  return STRUCTURE_TERM_SINGULAR[unitType] || 'Formation';
}

export function structureTermPlural(unitType) {
  return STRUCTURE_TERM_PLURAL[unitType] || 'Formations';
}

/** Default tactical field window per unit_type, used only to seed a NEW
 * formation's document.field_config — offense/defense get the standard
 * ±20yd LOS-centered view (widened from ±15yd in Phase B.3b §31 — "more
 * breathing room, routes don't visually fill the entire field
 * immediately"); special teams gets a substantially larger window since
 * kick/punt plays travel far past 40 total yards. Stored on the
 * formation's/Play's own document from creation on — never re-derived,
 * and never a global hardcoded invariant the renderer assumes.
 * PRESENTATION ONLY: before_los_yd/after_los_yd only ever change what
 * fraction of the fixed VIEW_WIDTH/VIEW_HEIGHT viewBox one yard occupies
 * (see fieldCoordinates.js's own fieldToScreen()) — no marker/path/zone
 * x/y coordinate anywhere is affected by, or needs to change because of,
 * this value. */
export function defaultFieldConfig(unitType) {
  if (unitType === 'special_teams') {
    return { before_los_yd: 20, after_los_yd: 60 };
  }
  return { before_los_yd: 20, after_los_yd: 20 };
}

/** Phase B.3b (§31/§32) — widens an EXISTING Play's field_config from
 * the legacy ±15yd offense/defense default to the new ±20yd one,
 * PRESENTATION ONLY (see defaultFieldConfig()'s own note above — y stays
 * "yards relative to LOS" regardless of window size, so this touches no
 * marker/path/zone coordinate at all). Only touches a field_config that
 * EXACTLY matches the OLD default (15/15) — a document whose
 * field_config was ever anything else (not reachable via any current
 * UI, but checked defensively) is left completely alone, so this can
 * never silently override an intentional value. Mirrors the existing
 * ensureTimeline()/ensurePostSnap()/ensurePreSnap() contract exactly:
 * normalizes the in-memory `doc` only at load time — nothing is
 * auto-persisted; the wider window is saved only if the coach hits Save
 * on this Play again. Scoped to the PLAY EDITOR only (see
 * TeamPlaybookPlayEditor.js's own call site) — Formation Editor's
 * already-saved Formations are left at whatever field_config they were
 * created with; only NEWLY created Formations pick up the wider default
 * via defaultFieldConfig() above. */
export function ensureWidePlayFieldConfig(doc) {
  const fc = doc.field_config;
  if (fc && fc.before_los_yd === 15 && fc.after_los_yd === 15) {
    return { ...doc, field_config: { ...fc, before_los_yd: 20, after_los_yd: 20 } };
  }
  return doc;
}

/** A brand-new, empty formation document — schema_version from day one
 * (the shape of this JSON blob may evolve later; this lets a future
 * reader distinguish old documents from new ones), no markers yet. */
export function emptyDocument(unitType) {
  return {
    schema_version: 1,
    field_config: defaultFieldConfig(unitType),
    markers: [],
  };
}

/** Human-readable "N v N" — never assumes 11. */
export function formatPlayersPerSide(playersPerSide) {
  if (!playersPerSide) return null;
  return `${playersPerSide}v${playersPerSide}`;
}

// Shared FormatPicker choice<->integer resolution — originally local to
// TeamPlaybookPage.js's FormationEditorView, promoted here once the Play
// editor (Phase B.0) needed the exact same logic, so both stay in sync
// with one implementation rather than two.
export function resolvePlayersPerSide(formatChoice, formatOtherText) {
  if (formatChoice === 'other') {
    const n = parseInt(formatOtherText, 10);
    return Number.isFinite(n) ? n : null;
  }
  if (!formatChoice) return null;
  const n = parseInt(formatChoice, 10);
  return Number.isFinite(n) ? n : null;
}

export function playersPerSideToChoice(pps) {
  if (pps === null || pps === undefined) return { choice: '', otherText: '' };
  if (FORMAT_OPTIONS.some(o => o.value === pps)) return { choice: String(pps), otherText: '' };
  return { choice: 'other', otherText: String(pps) };
}

// ─────────────────────────────────────────────────────────────────────
// Playbook Phase B.0 (035) — Play helpers. A Play's own book (unit_type)
// is always "our" side; the opponent's book is a separate, explicit
// value stored on the Play's own document (see migration 035's header
// comment) since it's never inferable from the Play's unit_type alone.
// ─────────────────────────────────────────────────────────────────────

/** The book a Play's OPPONENT look is drawn from. Offense faces a
 * defensive look and vice versa; Special Teams plays face another
 * Special Teams Unit (there is no separate "return team" book in this
 * architecture — both sides are saved under the same special_teams
 * book, just different rows/templates). */
export function opponentUnitTypeFor(unitType) {
  if (unitType === 'offense') return 'defense';
  if (unitType === 'defense') return 'offense';
  return 'special_teams';
}

/** A brand-new, empty Play document — schema_version 1 from day one
 * (a DIFFERENT schema than Formation's document.schema_version=1; same
 * number, different shape/meaning, versioned independently per document
 * TYPE, not shared). One shared field_config for both sides to render
 * into (from defaultFieldConfig(unitType), same helper Formation uses),
 * and two independent, empty marker sets — `our` and `opponent` — ready
 * to be filled by a snapshot copy, never a live reference.
 *
 * Phase B.1 (036 follow-up, no migration) adds `post_snap.assignments` —
 * still schema_version 1, deliberately NOT bumped. This is a purely
 * additive optional field: nothing about `our`/`opponent`/`field_config`
 * changed shape or meaning, and every reader treats a missing/absent
 * `post_snap` as "no assignments yet" rather than an error (see
 * ensurePostSnap() below) — exactly the kind of forward-compatible
 * addition schema_version=1 was meant to tolerate without a version
 * bump. A future genuinely-breaking document reshape would still get a
 * bump; this isn't that. */
// Phase B.3 (no migration — same additive philosophy as post_snap above)
// adds `pre_snap.assignments`: the Play's OPTIONAL Motion/Shift
// authoring, a Play-lifecycle phase that runs BEFORE Snap rather than
// after it. Deliberately its OWN sibling array, never reusing
// `post_snap.assignments` — Pre-Snap movement and Post-Snap
// responsibility are different football concepts with different
// authoring UIs (see TeamPlaybookPlayEditor.js's own Pre-Snap section),
// even though both happen to share the exact same
// `{ id, actor_side, actor_id, type, path }` shape and the same
// replace-by-actor semantics. Still schema_version 1 — purely additive,
// absent-tolerant (see ensurePreSnap() below), exactly like post_snap's
// own introduction.
//
// Phase B.3b (§3-§11) adds the SECOND, independent Pre-Snap concept:
// `pre_snap.set_positions` — a STATIC Pre-Snap alignment adjustment for
// an actor with NO Motion path ("by Snap, this player is set here,"
// never an authored path/movement). Deliberately its own sparse array,
// not folded into `assignments` (a set-position is a single point, not
// a `type`+`path` responsibility — giving it a fake `type:'motion'` with
// a one-point path would misrepresent it as authored Motion, exactly
// what §3/§38 of the B.3b spec forbids) and NOT reusing
// `timeline.actor_state` either (that mechanism is keyed by keyframe id
// — a Moment in time — whereas a set position isn't tied to any Moment
// at all, it's simply "the resolved position once Pre-Snap is done";
// see §42's own explicit "do not merge unless the architecture clearly
// supports a shared primitive while preserving separate semantics" —
// keyframe-keyed actor_state does NOT cleanly support a keyframe-less
// concept). Entry shape: `{ actor_side, actor_id, x, y }` — same
// football-relative coordinate convention as every other stored
// position in this document. Sparse: an actor with no override simply
// has no entry at all (§7 — "do not duplicate every marker into Pre-Snap
// state"); Formation position remains the baseline for every actor
// without one. See resolveSnapPosition() in TeamPlaybookPlayEditor.js
// for the full Motion > set_position > Formation precedence this
// enables.
// Phase B.3f (§6) added `post_snap.passing_reads`: a flat, Post-Snap-
// level `pass_read` cue array activated by the target receiver's own
// resolved ROUTE PROGRESS against a stored `{start_progress,
// end_progress}` window, authored via a two-tap "tap where the read
// begins/ends on the route" field flow. Phase B.3g REPLACED that model
// after further tablet-first browser testing showed the two-tap window
// authoring was too many interactions for a coaching tool ("scrub →
// +Passing Read → Read 1/2/3 → choose receiver → tap start → tap end →
// save" — too many taps for what should be "tap Read WR1, done").
//
// `post_snap.passing_reads` itself is now LEGACY — still present in
// `ensurePostSnap()`'s guaranteed shape below (any Play saved during the
// B.3f window loads without crashing, its old cues sitting there inert
// and unread), but no current authoring/rendering code reads or writes
// it any more. It is intentionally NOT migrated anywhere automatically
// (there is no safe destination to invent — see the current `pass_read`
// shape below, which requires a Moment id a B.3f cue never had) —
// "do not invent arbitrary Moment timing if none exists" applies just as
// much to this migration as it did to B.3f's own original one. A coach
// who wants a B.3f-era read back simply re-authors it fresh under the
// current workflow (a few taps); nothing about the old data blocks or
// interferes with that.
//
// THE CURRENT MODEL (B.3g): `pass_read` cues live on
// `timeline.keyframes[].cues` — see playbookTimeline.js's own header
// note for the full history of that field moving OUT (B.3f) and back IN
// (B.3g). A Passing Read is once again genuinely OWNED by the Moment
// that answers "WHEN does the QB evaluate this read?" — deleting that
// Moment deletes its reads with it, for free, via the exact same
// deleteKeyframe() every other Moment already used. What B.3f got right
// and B.3g keeps: the dashed cue line still points at a route-attached
// LANDMARK, never the receiver's marker — just via a single captured
// `route_progress` (the target's resolved route progress AT the owning
// Moment, captured once when the coach taps "Read WR1," never
// re-evaluated automatically afterward) instead of a two-point window.
// Cue shape:
//   { id, type: 'pass_read',
//     actor_side, actor_id,             // the QB — same field names as
//       every other assignment's own "who does this belong to" pair.
//     progression: 1 | 2 | 3,           // mirrors Route's own persisted `progression`, never re-derives it
//     target_side, target_actor_id,     // the coach's CHOSEN receiver — stored, never silently re-targeted
//     route_progress }                  // OPTIONAL — the captured landmark position (0..1 along the
//       target's route, resolved at the owning Moment). Absent only for
//       a genuinely legacy B.3e-shaped cue loaded as-is (B.3e's own
//       shape is a strict subset of this one, missing only
//       route_progress — see TeamPlaybookPlayEditor.js's own
//       describeMomentReadCue() for how that's surfaced as "needs read
//       point" rather than crashing or being silently discarded).
// Same flat, type-discriminated, future-extensible design first
// established back in B.3e — a future `block_read`/`rpo_read`/
// `run_read`/generic Path Action cue type is just another entry in this
// SAME per-Moment array.
export function emptyPlayDocument(unitType) {
  return {
    schema_version: 1,
    field_config: defaultFieldConfig(unitType),
    our: { source_name: null, markers: [] },
    opponent: { unit_type: opponentUnitTypeFor(unitType), source_name: null, markers: [] },
    post_snap: { assignments: [], passing_reads: [] },
    pre_snap: { assignments: [], set_positions: [] },
  };
}

/** Backfills `post_snap` on a Play document loaded from before Phase
 * B.1 (assignments) / B.3f (passing_reads) existed — never mutates the
 * input, always returns an object with well-formed arrays so downstream
 * code never has to null-check either one. `passing_reads` itself is
 * kept for backward-load-compatibility only (see this file's own
 * up-to-date header note on `pass_read` cues above) — still guaranteed
 * to exist as an array so nothing crashes reading it, but no current
 * code writes new entries into it; B.3g's Passing Read model lives on
 * `timeline.keyframes[].cues` instead. */
export function ensurePostSnap(doc) {
  const existing = doc.post_snap;
  if (existing && Array.isArray(existing.assignments) && Array.isArray(existing.passing_reads)) return doc;
  return {
    ...doc,
    post_snap: {
      assignments: Array.isArray(existing?.assignments) ? existing.assignments : [],
      passing_reads: Array.isArray(existing?.passing_reads) ? existing.passing_reads : [],
    },
  };
}

/** Backfills `pre_snap` on a Play document loaded from before Phase B.3
 * existed — same pattern/guarantee as ensurePostSnap() above. Every Play
 * saved before this phase has no `pre_snap` key at all; this makes it
 * safe to read `doc.pre_snap.assignments` unconditionally everywhere
 * else in the Pre-Snap authoring/resolution code, and — critically — an
 * absent/empty `pre_snap.assignments` is exactly what makes an existing
 * Play behave IDENTICALLY to before this phase existed (every actor has
 * no Pre-Snap movement, so Formation position IS the Snap position, the
 * same fallback rule §10 of the B.3 spec requires). */
export function ensurePreSnap(doc) {
  const existing = doc.pre_snap;
  if (existing && Array.isArray(existing.assignments) && Array.isArray(existing.set_positions)) return doc;
  return {
    ...doc,
    pre_snap: {
      assignments: Array.isArray(existing?.assignments) ? existing.assignments : [],
      set_positions: Array.isArray(existing?.set_positions) ? existing.set_positions : [],
    },
  };
}

/** Deep-copies a Formation/Front/Unit's saved markers into a fresh
 * marker array with brand-new ids — used both for "our" and "opponent"
 * snapshots. Takes markers directly (not the whole Formation row) so it
 * works identically whether the source is a saved playbook_formations
 * row or a starter template's plain marker list. */
export function snapshotMarkers(markers, newMarkerId) {
  return (markers || []).map(m => ({
    id: newMarkerId(), role_label: m.role_label, start: { x: m.start ? m.start.x : m.x, y: m.start ? m.start.y : m.y },
  }));
}
